import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle, X, Plus, GitCompare } from "lucide-react";
import { useCompare } from "../lib/compareStore";
import { getMotorBySlug, MOTORCYCLES } from "../data/motorcycles";
import { formatIDR, buildWaLink, buildWaLinkGeneric } from "../lib/whatsapp";

const SPEC_KEYS = ["Mesin", "Tenaga", "Transmisi", "Tangki", "Berat", "Fitur"];

export default function Compare() {
  const navigate = useNavigate();
  const { slugs, remove, clear, toggle, has, max } = useCompare();
  const items = slugs.map(getMotorBySlug).filter(Boolean);

  const cheapestPrice = items.length ? Math.min(...items.map((m) => m.price_idr)) : 0;

  const compareMsg = items.length
    ? `Halo, saya sedang membandingkan motor Yamaha berikut:\n${items.map((m, i) => `${i + 1}. ${m.name} - ${formatIDR(m.price_idr)}`).join("\n")}\n\nMohon rekomendasi mana yang paling cocok untuk saya. Terima kasih.`
    : "";

  const canAddMore = items.length < max;
  const suggestions = MOTORCYCLES
    .filter((m) => !slugs.includes(m.slug))
    .slice(0, 6);

  return (
    <div className="py-6 md:py-10 bg-white min-h-[60vh]" data-testid="page-compare">
      <div className="y-container">
        <div className="flex items-center justify-between gap-2 mb-4">
          <Link
            to="/"
            data-testid="compare-back"
            className="inline-flex items-center gap-1 text-xs sm:text-sm text-slate-600 hover:text-[#002d62] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Katalog
          </Link>
          {items.length > 0 && (
            <button
              onClick={clear}
              data-testid="compare-clear-all"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-slate-600 hover:text-white hover:bg-slate-800 border border-slate-200 hover:border-slate-800 transition-colors"
            >
              <X className="w-3 h-3" /> Kosongkan
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#002d62] text-white">
            <GitCompare className="w-4 h-4" />
          </span>
          <h1 className="font-display font-extrabold text-lg sm:text-2xl text-slate-900">
            Bandingkan Motor
          </h1>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 py-14 text-center">
            <GitCompare className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-600">Belum ada motor untuk dibandingkan.</p>
            <p className="text-xs text-slate-500 mt-1">Pilih minimal 2 motor dari katalog dengan menekan tombol "Compare" di tiap kartu.</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#002d62] hover:bg-[#001a3f] text-white text-sm font-semibold transition"
              data-testid="compare-goto-catalog"
            >
              Pilih Motor
            </button>
          </div>
        ) : (
          <>
            {/* Compare table - horizontal scroll on mobile */}
            <div className="overflow-x-auto -mx-3 sm:mx-0 no-scrollbar" data-testid="compare-table">
              <div className="min-w-[520px] px-3 sm:px-0">
                {/* Motor cards row */}
                <div
                  className="grid gap-3 sm:gap-4"
                  style={{ gridTemplateColumns: `120px repeat(${Math.max(items.length, 2)}, minmax(160px, 1fr))` }}
                >
                  <div /> {/* empty top-left cell */}
                  {items.map((m) => (
                    <motion.div
                      key={m.slug}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="relative rounded-2xl bg-slate-50 border border-slate-200 p-3 text-center"
                      data-testid={`compare-card-${m.slug}`}
                    >
                      <button
                        onClick={() => remove(m.slug)}
                        data-testid={`compare-remove-header-${m.slug}`}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center shadow-md hover:bg-black transition"
                        aria-label="Hapus"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="aspect-[4/3] bg-white rounded-lg overflow-hidden mb-2">
                        <img src={m.image} alt={m.name} className="w-full h-full object-contain p-2" />
                      </div>
                      <p className="font-display font-bold text-xs sm:text-sm text-slate-900 line-clamp-1">
                        {m.name}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{m.category}</p>
                      <p
                        className={`mt-1 font-display font-extrabold text-sm ${
                          m.price_idr === cheapestPrice ? "text-emerald-600" : "text-[#002d62]"
                        }`}
                      >
                        {formatIDR(m.price_idr)}
                      </p>
                      {m.price_idr === cheapestPrice && items.length > 1 && (
                        <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase">
                          Termurah
                        </span>
                      )}
                    </motion.div>
                  ))}
                  {/* placeholder for add-more slot */}
                  {canAddMore && items.length < 2 && (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300 min-h-[160px]">
                      <Plus className="w-6 h-6" />
                    </div>
                  )}
                </div>

                {/* Spec rows */}
                <div className="mt-4 rounded-2xl border border-slate-200 overflow-hidden bg-white">
                  {[
                    { key: "priceLabel", label: "Label Harga", getValue: (m) => m.priceLabel },
                    { key: "engine_cc", label: "Kapasitas", getValue: (m) => `${m.engine_cc}cc` },
                    ...SPEC_KEYS.map((k) => ({ key: k, label: k, getValue: (m) => m.specs?.[k] || "-" })),
                  ].map((row, idx) => (
                    <div
                      key={row.key}
                      className={`grid gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 text-xs sm:text-sm ${
                        idx % 2 === 0 ? "bg-slate-50" : "bg-white"
                      }`}
                      style={{ gridTemplateColumns: `120px repeat(${Math.max(items.length, 2)}, minmax(160px, 1fr))` }}
                    >
                      <div className="font-semibold text-slate-500 text-[11px] sm:text-xs uppercase tracking-wider flex items-center">
                        {row.label}
                      </div>
                      {items.map((m) => (
                        <div key={m.slug + row.key} className="text-slate-800">
                          {row.getValue(m)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Action row */}
                <div
                  className="mt-3 grid gap-3 sm:gap-4"
                  style={{ gridTemplateColumns: `120px repeat(${Math.max(items.length, 2)}, minmax(160px, 1fr))` }}
                >
                  <div />
                  {items.map((m) => (
                    <a
                      key={"wa-" + m.slug}
                      href={buildWaLink(m.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`compare-wa-${m.slug}`}
                      className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-[#25D366] hover:bg-[#1ebe57] text-white text-[11px] sm:text-xs font-semibold transition"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Pesan
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-5 rounded-2xl bg-gradient-to-br from-[#002d62] to-[#0a1f44] text-white p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-3 justify-between">
              <div>
                <p className="font-display font-bold text-sm sm:text-base">Masih bingung pilih?</p>
                <p className="text-xs text-white/70">Konsultasi langsung, saya bantu rekomendasi yang cocok untuk kebutuhanmu.</p>
              </div>
              <a
                href={buildWaLinkGeneric(compareMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="compare-consult-wa"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-semibold transition"
              >
                <MessageCircle className="w-4 h-4" /> Tanya Rekomendasi
              </a>
            </div>

            {/* Add more suggestions */}
            {canAddMore && (
              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                  Tambah motor untuk dibandingkan ({items.length}/{max})
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                  {suggestions.map((m) => (
                    <button
                      key={m.slug}
                      onClick={() => toggle(m.slug)}
                      data-testid={`compare-add-${m.slug}`}
                      className="group rounded-xl border border-slate-200 hover:border-[#002d62] hover:shadow p-1.5 sm:p-2 text-left transition"
                    >
                      <div className="aspect-[4/3] bg-white rounded overflow-hidden">
                        <img src={m.image} alt={m.name} loading="lazy" className="w-full h-full object-contain group-hover:scale-105 transition-transform p-1" />
                      </div>
                      <p className="text-[10px] sm:text-xs font-bold text-slate-800 line-clamp-1 mt-1">{m.name}</p>
                      <p className="text-[9px] sm:text-[10px] text-slate-500">{formatIDR(m.price_idr)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
